import { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { DataGrid, CustomGridColDef } from "components/DataGrid";
import { formatAmountInPounds } from "util/formatNumber";
import { prettyPrintWasteClassification } from "../../../prettyPrintWasteClassification";
import { profileNumberValueGetter } from "../WasteStreamsDataGrid";
import { WasteStream } from "generated-graphql/graphql";

interface MergedWasteStreamsDataGridProps {
  wasteStreams: WasteStream[]; // Replace with proper type when available
  dateRangeText: string;
  loading?: boolean;
}

export const MergedWasteStreamsDataGrid = ({
  wasteStreams,
  dateRangeText,
  loading,
}: MergedWasteStreamsDataGridProps) => {
  const theme = useTheme();

  const columns: CustomGridColDef<WasteStream>[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Waste Stream",
        flex: 1,
        minWidth: 150,
        headerClassName: "wrapHeader",
      },
      {
        field: "profileNumber",
        headerName: "Profile #",
        flex: 0.8,
        headerClassName: "wrapHeader",
        valueGetter: profileNumberValueGetter,
      },
      {
        field: "federalWasteCodes",
        headerName: "Federal Waste Code",
        flex: 0.6,
        headerClassName: "wrapHeader",
        valueGetter: (_, row) => {
          const federalWasteCodes = row.federalWasteCodes || [];
          return federalWasteCodes.join(", ");
        },
      },
      {
        field: "stateWasteCodes",
        headerName: "State Waste Code",
        flex: 0.6,
        headerClassName: "wrapHeader",
        valueGetter: (_, row) => {
          const stateWasteCodes = row.stateWasteCodes || [];
          return stateWasteCodes.join(", ");
        },
      },
      {
        field: "classification",
        headerName: "Classification",
        flex: 0.7,
        headerClassName: "wrapHeader",
        valueFormatter: (_, row) =>
          prettyPrintWasteClassification(row.classification),
      },
      {
        field: "formCode",
        headerName: "Form Code",
        flex: 0.4,
        headerClassName: "wrapHeader",
      },
      {
        field: "sourceCode",
        headerName: "Source Code",
        flex: 0.4,
        headerClassName: "wrapHeader",
      },
      {
        field: "dateRangeAmount",
        headerName: `Amount (${dateRangeText})`,
        flex: 0.6,
        headerClassName: "wrapHeader",
        renderHeader: () => (
          <Box>
            <Typography fontWeight={500} variant="body2" lineHeight={"normal"}>
              Amount
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: theme.spacing(1.25),
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
            >
              [{dateRangeText}]
            </Typography>
          </Box>
        ),
        valueFormatter: (_, row) =>
          formatAmountInPounds(row.vendorAmount || row.eManifestAmount),
      },
      {
        field: "allTimeAmount",
        headerName: "Amount",
        flex: 0.6,
        headerClassName: "wrapHeader",
        renderHeader: () => (
          <Box>
            <Typography fontWeight={500} variant="body2" lineHeight={"normal"}>
              Amount
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: theme.spacing(1.25) }}
            >
              [all time]
            </Typography>
          </Box>
        ),
        valueFormatter: (_, row) =>
          formatAmountInPounds(
            row.vendorAmountAllTime || row.eManifestAmountAllTime
          ),
      },
      {
        field: "comment",
        headerName: "Comment",
        flex: 0.8,
        headerClassName: "wrapHeader",
        valueGetter: (_, row) => "", // TODO: implement comment
      },
    ];
  }, [dateRangeText, theme]);

  return (
    <DataGrid
      rows={wasteStreams}
      columns={columns}
      loading={loading}
      hideFooter
      disableColumnMenu
      disableColumnSelector
      disableRowSelectionOnClick
      getRowId={(row) => row.id}
      columnHeaderHeight={75} // 14rem in MUI units
      sx={{
        "& .wrapHeader .MuiDataGrid-columnHeaderTitleContainer": {
          alignItems: "flex-start",
          py: theme.spacing(1),
        },
      }}
    />
  );
};
